import { useCallback, useEffect, useRef, useState } from 'react'
import { Field } from '../components/Form'
import { Sheet, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { useOccurrence, useSessions } from '../data/hooks'
import { formatDayLong, parseIsoDate, parseSessionKey } from '../data/schedule'
import { clock, minutesOf } from '../data/scheduleEdit'
import { SliceApiError, type OccurrencePreview } from '../data/types'
import { isOnline } from '../platform/network'

/**
 * Шторки действий с одним занятием (S05, этап 3 шаг 10, SC13): отмена,
 * возврат и перенос. Сначала предпросмотр с сервера - что именно изменится
 * или почему нельзя; подтверждение - только когда можно. Повторное нажатие,
 * пока запрос в полёте, второй запрос не шлёт; повтор после сбоя безопасен -
 * сервер отвечает replayed.
 */

type OccurrenceApi = ReturnType<typeof useOccurrence>

/** «1 отметка», «3 отметки», «5 отметок» */
export function pluralMarks(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return `${n} отметка`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} отметки`
  return `${n} отметок`
}

/** «Среда, 7 октября, 17:00 - 18:00, Зал 1» */
function describeSlot(o: { date: string; startTime: string; endTime?: string; location?: string }): string {
  const d = parseIsoDate(o.date)
  const day = d ? formatDayLong(d) : o.date
  const time = o.endTime ? `${o.startTime} - ${o.endTime}` : o.startTime
  return [day, time, o.location].filter(Boolean).join(', ')
}

/** Почему нельзя - понятным текстом. */
function refusalText(p: OccurrencePreview): string {
  const marks = pluralMarks(p.occurrence.marks)
  switch (p.reason) {
    case 'has_marks':
      return p.action === 'move'
        ? `На занятии уже ${marks}. Перенести можно после того, как отметки сняты; сами отметки не меняются.`
        : `На занятии уже ${marks}. Отменить можно после того, как отметки сняты; сами отметки не меняются.`
    case 'already_cancelled':
      return 'Занятие уже отменено.'
    case 'not_cancelled':
      return 'Занятие не отменено, возвращать нечего.'
    case 'cancelled':
      return 'Занятие отменено, переносить нечего. Сначала верните его.'
    case 'slot_taken':
      return 'В это время у группы уже есть занятие. Выберите другой день или время.'
    case 'same_slot':
      return 'Это тот же день и то же время. Измените дату или время.'
    default:
      return 'Сейчас это сделать нельзя.'
  }
}

/** Текст сбоя записи. null - конфликт: предпросмотр перечитывается. */
function writeErrorText(err: unknown): string | null {
  const e = err as SliceApiError
  if (e?.kind === 'conflict' || (e instanceof SliceApiError && e.message === 'same_slot')) return null
  if (e?.kind === 'not_found') return 'Занятие не найдено: возможно, его уже перенесли или убрали из расписания.'
  if (e?.kind === 'limit') return 'Клуб сейчас только для чтения: подписка закончилась. Ничего не изменено.'
  if (e?.kind === 'network' && !isOnline()) return 'Нет сети - не сохранено, можно повторить.'
  return 'Не сохранилось, можно повторить.'
}

function loadErrorText(err: unknown): string {
  const e = err as SliceApiError
  if (e?.kind === 'not_found') return 'Занятие не найдено: возможно, его уже перенесли или убрали из расписания.'
  return 'Не удалось проверить занятие.'
}

type ConfirmState =
  | { phase: 'loading' }
  | { phase: 'load_error'; message: string; retry: boolean }
  | { phase: 'ready'; preview: OccurrencePreview; notice?: string }
  | { phase: 'saving'; preview: OccurrencePreview }
  | { phase: 'failed'; preview: OccurrencePreview; message: string; final: boolean }

/** Отмена или возврат одного занятия: предпросмотр, подтверждение, итог. */
export function CancelRestoreSheet({
  kind,
  occurrence,
  onClose,
}: {
  kind: 'cancel' | 'restore'
  occurrence: OccurrenceApi
  onClose: () => void
}) {
  const { toast } = useEnv()
  const [state, setState] = useState<ConfirmState>({ phase: 'loading' })
  // Ответ после закрытия шторки не нужен. Флаг взводится в эффекте: в
  // StrictMode эффект монтируется дважды.
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  const load = useCallback(
    async (notice?: string) => {
      setState({ phase: 'loading' })
      try {
        const preview = await occurrence.preview({ action: kind })
        if (alive.current) setState({ phase: 'ready', preview, notice })
      } catch (err) {
        if (alive.current) {
          setState({ phase: 'load_error', message: loadErrorText(err), retry: (err as SliceApiError)?.kind !== 'not_found' })
        }
      }
    },
    [kind, occurrence],
  )

  useEffect(() => {
    void load()
  }, [load])

  const confirm = async (preview: OccurrencePreview) => {
    setState({ phase: 'saving', preview })
    try {
      const res = kind === 'cancel' ? await occurrence.cancel() : await occurrence.restore()
      if (!res) return // запрос уже в полёте
      if (kind === 'cancel') toast(res.replayed ? 'Занятие уже было отменено' : 'Занятие отменено')
      else toast(res.replayed ? 'Занятие уже было возвращено' : 'Занятие возвращено')
      onClose()
    } catch (err) {
      const message = writeErrorText(err)
      if (message === null) {
        await load('Пока вы смотрели, занятие изменилось. Проверьте ещё раз.')
        return
      }
      setState({ phase: 'failed', preview, message, final: (err as SliceApiError)?.kind !== 'network' && (err as SliceApiError)?.kind !== 'server' })
    }
  }

  const title = kind === 'cancel' ? 'Отменить занятие?' : 'Вернуть занятие?'
  const saving = state.phase === 'saving'

  return (
    <Sheet title={title} onClose={saving ? () => {} : onClose}>
      {state.phase === 'loading' ? (
        <Skeleton rows={2} label="Проверяю занятие" />
      ) : state.phase === 'load_error' ? (
        <>
          <div className="pc-alert" role="alert">
            {state.message}
          </div>
          {state.retry && (
            <button type="button" className="pc-btn pc-btn--block" style={{ justifyContent: 'center' }} onClick={() => void load()}>
              Повторить
            </button>
          )}
          <button type="button" className="pc-btn pc-btn--block" style={{ justifyContent: 'center' }} onClick={onClose}>
            Закрыть
          </button>
        </>
      ) : (
        <>
          {state.phase === 'ready' && state.notice && (
            <div className="pc-note" role="status" style={{ marginTop: 4 }}>
              {state.notice}
            </div>
          )}
          <p style={{ margin: '8px 0 0', fontSize: 15 }}>
            {kind === 'cancel' ? 'Отменяется только это занятие: ' : 'Занятие снова будет в расписании: '}
            <strong>{describeSlot(state.preview.occurrence)}</strong>.
          </p>
          <p className="pc-muted" style={{ margin: '8px 0 0', fontSize: 14 }}>
            Постоянное расписание группы не меняется.
          </p>

          {!state.preview.allowed && (
            <div className="pc-alert" role="alert">
              {refusalText(state.preview)}
            </div>
          )}
          {state.phase === 'failed' && (
            <div className="pc-alert" role="alert">
              {state.message}
            </div>
          )}

          {state.preview.allowed && !(state.phase === 'failed' && state.final) && (
            <button
              type="button"
              className={`pc-btn pc-btn--block${kind === 'cancel' ? ' pc-btn--danger' : ' pc-btn--primary'}`}
              style={{ justifyContent: 'center', marginTop: 16 }}
              disabled={saving}
              onClick={() => void confirm(state.preview)}
            >
              {saving
                ? 'Сохраняю…'
                : state.phase === 'failed'
                  ? 'Повторить'
                  : kind === 'cancel'
                    ? 'Отменить занятие'
                    : 'Вернуть занятие'}
            </button>
          )}
          <button type="button" className="pc-btn pc-btn--block" style={{ justifyContent: 'center' }} disabled={saving} onClick={onClose}>
            {state.preview.allowed ? (kind === 'cancel' ? 'Не отменять' : 'Не возвращать') : 'Закрыть'}
          </button>
        </>
      )}
    </Sheet>
  )
}

type MoveState =
  | { phase: 'form'; error?: string }
  | { phase: 'checking' }
  | { phase: 'preview'; preview: OccurrencePreview; notice?: string }
  | { phase: 'saving'; preview: OccurrencePreview }
  | { phase: 'failed'; preview: OccurrencePreview; message: string; final: boolean }

function hhmm(total: number): string {
  const m = ((total % 1440) + 1440) % 1440
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

/**
 * Перенос одного занятия: дата, начало, конец (по умолчанию - то же время и та
 * же длительность). «Проверить» - предпросмотр «Было / Станет»; подтверждение -
 * переход на новое занятие. Ввод не теряется при сбое.
 */
export function MoveSheet({
  sessionKey,
  occurrence,
  today,
  onClose,
  onMoved,
}: {
  sessionKey: string
  occurrence: OccurrenceApi
  today: string
  onClose: () => void
  onMoved: (toKey: string) => void
}) {
  const { toast } = useEnv()
  const parsed = parseSessionKey(sessionKey)
  const sourceDate = parsed?.date ?? today
  // Длительность исходного занятия - из расписания дня (на доске конца нет).
  const day = useSessions(sourceDate, sourceDate)
  const slot = day.data?.find((s) => s.key === sessionKey)
  const sourceStart = minutesOf(parsed?.startTime)
  const sourceEnd = minutesOf(slot?.endTime)
  const duration = sourceStart !== null && sourceEnd !== null && sourceEnd > sourceStart ? sourceEnd - sourceStart : null

  const [date, setDate] = useState(sourceDate >= today ? sourceDate : today)
  const [start, setStart] = useState(parsed?.startTime ?? '')
  const [end, setEnd] = useState('')
  const [endTouched, setEndTouched] = useState(false)
  const [state, setState] = useState<MoveState>({ phase: 'form' })
  // Ответ после закрытия шторки не нужен. Флаг взводится в эффекте: в
  // StrictMode эффект монтируется дважды.
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  // Конец по умолчанию - та же длительность от выбранного начала, пока его не трогали.
  useEffect(() => {
    if (endTouched || duration === null) return
    const s = minutesOf(start)
    setEnd(s === null ? '' : hhmm(s + duration))
  }, [duration, endTouched, start])

  const startMin = minutesOf(start)
  const endMin = minutesOf(end)
  const dateError = !parseIsoDate(date) ? 'Укажите дату' : date < today ? 'Дата не раньше сегодняшнего дня' : undefined
  const startError = startMin === null ? 'Укажите начало' : undefined
  const endError = end && (endMin === null || (startMin !== null && endMin <= startMin)) ? 'Конец должен быть позже начала' : undefined
  const invalid = !!(dateError || startError || endError)

  const edit = (apply: () => void) => {
    apply()
    // Ввод изменился - прежний предпросмотр уже не про него.
    setState((st) => (st.phase === 'saving' || st.phase === 'checking' ? st : { phase: 'form' }))
  }

  const to = () => ({ date, startTime: clock(start), endTime: end ? clock(end) : undefined })

  const check = async (notice?: string) => {
    if (invalid) return
    setState({ phase: 'checking' })
    const t = to()
    try {
      const preview = await occurrence.preview({ action: 'move', toDate: t.date, toStart: t.startTime, toEnd: t.endTime })
      if (alive.current) setState({ phase: 'preview', preview, notice })
    } catch (err) {
      if (alive.current) setState({ phase: 'form', error: loadErrorText(err) })
    }
  }

  const confirm = async (preview: OccurrencePreview) => {
    setState({ phase: 'saving', preview })
    try {
      const res = await occurrence.move(to())
      if (!res) return // запрос уже в полёте
      toast(res.replayed ? 'Занятие уже было перенесено' : 'Занятие перенесено')
      onMoved(res.toKey)
    } catch (err) {
      const message = writeErrorText(err)
      if (message === null) {
        await check('Пока вы смотрели, занятия группы изменились. Проверьте ещё раз.')
        return
      }
      const kind = (err as SliceApiError)?.kind
      setState({ phase: 'failed', preview, message, final: kind !== 'network' && kind !== 'server' })
    }
  }

  const busy = state.phase === 'checking' || state.phase === 'saving'
  const shown = state.phase === 'preview' || state.phase === 'saving' || state.phase === 'failed' ? state : null

  return (
    <Sheet title="Перенести занятие" onClose={state.phase === 'saving' ? () => {} : onClose}>
      <p className="pc-muted" style={{ margin: '0 0 12px', fontSize: 14 }}>
        Переносится только это занятие. Исходное будет отменено, на новое время заведётся разовое. Постоянное расписание не меняется.
      </p>
      <form
        className="pc-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          void check()
        }}
      >
        <Field label="Дата" error={dateError}>
          {(p) => (
            <input
              {...p}
              className="pc-input"
              type="date"
              min={today}
              value={date}
              disabled={busy}
              onChange={(e) => edit(() => setDate(e.target.value))}
            />
          )}
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'start' }}>
          <Field label="Начало" error={startError}>
            {(p) => (
              <input
                {...p}
                className="pc-input"
                type="time"
                value={start}
                disabled={busy}
                onChange={(e) => edit(() => setStart(e.target.value))}
              />
            )}
          </Field>
          <Field label="Конец" error={endError} hint={end ? undefined : 'Не указан: длительность сохранится'}>
            {(p) => (
              <input
                {...p}
                className="pc-input"
                type="time"
                value={end}
                disabled={busy}
                onChange={(e) =>
                  edit(() => {
                    setEndTouched(true)
                    setEnd(e.target.value)
                  })
                }
              />
            )}
          </Field>
        </div>

        {state.phase === 'form' && state.error && (
          <div className="pc-alert" role="alert">
            {state.error}
          </div>
        )}

        {!shown && (
          <button
            type="submit"
            className="pc-btn pc-btn--primary pc-btn--block"
            style={{ justifyContent: 'center', marginTop: 12 }}
            disabled={invalid || busy}
          >
            {state.phase === 'checking' ? 'Проверяю…' : 'Проверить'}
          </button>
        )}
      </form>

      {shown && (
        <section aria-label="Что изменится" style={{ marginTop: 12 }}>
          {shown.phase === 'preview' && shown.notice && (
            <div className="pc-note" role="status">
              {shown.notice}
            </div>
          )}
          <ul className="pc-list pc-changes" aria-label="Было и станет">
            <li className="pc-change">
              <span className="pc-change__who">Было</span>
              <span className="pc-change__what">{describeSlot(shown.preview.occurrence)}</span>
            </li>
            {shown.preview.target && (
              <li className="pc-change">
                <span className="pc-change__who">Станет</span>
                <span className="pc-change__what">{describeSlot(shown.preview.target)}</span>
              </li>
            )}
          </ul>
          {shown.preview.allowed && shown.preview.target?.restoresCancelled && (
            <div className="pc-aa-note" role="note">
              В это время у группы было отменённое занятие: оно вернётся, нового не заведётся.
            </div>
          )}
          {!shown.preview.allowed && (
            <div className="pc-alert" role="alert">
              {refusalText(shown.preview)}
            </div>
          )}
          {shown.phase === 'failed' && (
            <div className="pc-alert" role="alert">
              {shown.message}
            </div>
          )}
          {shown.preview.allowed && !(shown.phase === 'failed' && shown.final) && (
            <button
              type="button"
              className="pc-btn pc-btn--primary pc-btn--block"
              style={{ justifyContent: 'center', marginTop: 16 }}
              disabled={shown.phase === 'saving'}
              onClick={() => void confirm(shown.preview)}
            >
              {shown.phase === 'saving' ? 'Переношу…' : shown.phase === 'failed' ? 'Повторить' : 'Перенести занятие'}
            </button>
          )}
        </section>
      )}
      <button
        type="button"
        className="pc-btn pc-btn--block"
        style={{ justifyContent: 'center' }}
        disabled={state.phase === 'saving'}
        onClick={onClose}
      >
        Не переносить
      </button>
    </Sheet>
  )
}
